import {
  MaterialLayout,
  Layout,
  QuoteRequestLine,
  ProductLine,
  ProcessStep,
  ExtendedPart,
  PartTypeSchema,
} from "../types";
import { round } from "../utils";
import { computeConsummablePrice } from "./computeConsummablePrice";
import { computeGeneralPrice } from "./computeGeneralPrice";
import { ComputationOptions, computeMaterialPrice } from "./computeMaterialPrice";
import { OptimizedMachineTimes } from "./computeOptimizedMachineTimes";
import { OptimizedMaterialCosts } from "./computeOptimizedMaterialCosts";
import { computeProcessesPrice } from "./computeProcessesPrice";

export type QuoteRequestLineForComputing = QuoteRequestLine & {
  productLines: ProductLine[];
  part: ExtendedPart;
  layout:
    | (Layout & {
        materialLayout: MaterialLayout | null;
      })
    | null;
  processSteps: (ProcessStep & {
    materialLayout: MaterialLayout | null;
    productLines: ProductLine[];
  })[];
};

type Params = {
  quoteRequestLine: QuoteRequestLineForComputing;
  isQuotePrice?: boolean;
  optimizedMachineTimes: OptimizedMachineTimes;
  optimizedMaterialCosts: OptimizedMaterialCosts;
};

export type QuoteRequestLinePrice = {
  partPrice: number;
  partCost: number;
  partMarginPrice: number;
  seriesPrice: number;
  seriesCost: number;
  seriesMarginPrice: number;
  assembly: {
    addedValueProcessesCost: number;
    productsProcessesCost: number;
    materialCost: number;
    totalCost: number;
    totalMargin: number;
    totalPrice: number;
  };
  costs: {
    addedValueProcessesCost: number;
    generalCost: number;
    materialCost: number;
    partAddedValueProcessesCost: number;
    partMaterialCost: number;
    partProductsProcessesCost: number;
    processesCost: number;
    productsProcessesCost: number;
  };
  margins: {
    addedValueProcessesMargin: number;
    generalMarginPrice: number;
    materialMarginPrice: number;
    partAddedValueProcessesMarginPrice: number;
    partMaterialMarginPrice: number;
    partProductsProcessesMarginPrice: number;
    processesMargin: number;
    productsProcessesMargin: number;
  };
  prices: {
    addedValueProcessesPrice: number;
    generalPrice: number;
    materialPrice: number;
    partAddedValueProcessesPrice: number;
    partMaterialPrice: number;
    partProductsProcessesPrice: number;
    processesPrice: number;
    productsProcessesPrice: number;
  };
  quantity: number;
};

export const computeQuoteRequestLinePrice = (
  { quoteRequestLine, isQuotePrice, optimizedMachineTimes, optimizedMaterialCosts }: Params,
  options: ComputationOptions,
): QuoteRequestLinePrice => {
  if (quoteRequestLine.part.type === PartTypeSchema.Enum.CONSUMABLE) {
    return computeConsummablePrice({ quoteRequestLine, isQuotePrice });
  }
  const quantityInAssembly = quoteRequestLine.part.quantityInAssembly ?? 1;
  const { partGeneralPrice, partGeneralCost, partGeneralMarginPrice } =
    computeGeneralPrice(quoteRequestLine);

  const { partMaterialPrice, partMaterialCost, partMaterialMarginPrice } = computeMaterialPrice(
    { quoteRequestLine, optimizedMaterialCosts },
    options,
  );

  const {
    partProcessesPrice,
    partProcessesCost,
    partProcessesMarginPrice,
    prices: { partProductsProcessesPrice, partAddedValueProcessesPrice },
    costs: { partProductsProcessesCost, partAddedValueProcessesCost },
    margins: { partProductsProcessesMarginPrice, partAddedValueProcessesMarginPrice },
  } = computeProcessesPrice({
    quantity: quoteRequestLine.quantity,
    reworkRatio: quoteRequestLine.reworkRatio,
    quantityInAssembly: quoteRequestLine.part.quantityInAssembly,
    addedValueMarginRatio: quoteRequestLine.addedValueMarginRatio,
    productsMarginRatio: quoteRequestLine.productsMarginRatio,
    processSteps: quoteRequestLine.processSteps,
    optimizedMachineTimes,
  });

  const partCost = isQuotePrice
    ? round(partGeneralCost + partMaterialCost + partProcessesCost)
    : partGeneralCost + partMaterialCost + partProcessesCost;

  const partPrice = isQuotePrice
    ? round(partGeneralPrice + partMaterialPrice + partProcessesPrice)
    : partGeneralPrice + partMaterialPrice + partProcessesPrice;

  const partMarginPrice = isQuotePrice
    ? round(partGeneralMarginPrice + partMaterialMarginPrice + partProcessesMarginPrice)
    : partGeneralMarginPrice + partMaterialMarginPrice + partProcessesMarginPrice;

  const seriesMultiplier = quoteRequestLine.quantity * quantityInAssembly;
  const assemblyCost = partCost * quantityInAssembly;
  const assemblyMargin = partMarginPrice * quantityInAssembly;
  const assemblyPrice = partPrice * quantityInAssembly;

  const assemblyAddedValueProcessesCost = partAddedValueProcessesCost * quantityInAssembly;
  const assemblyProductsProcessesCost = partProductsProcessesCost * quantityInAssembly;
  const assemblyMaterialCost = partMaterialCost * quantityInAssembly;

  const seriesPrice = partPrice * seriesMultiplier;
  const seriesCost = partCost * seriesMultiplier;
  const seriesMarginPrice = partMarginPrice * seriesMultiplier;

  const costs = {
    addedValueProcessesCost: partAddedValueProcessesCost * seriesMultiplier,
    generalCost: partGeneralCost * seriesMultiplier,
    materialCost: partMaterialCost * seriesMultiplier,
    partAddedValueProcessesCost,
    partMaterialCost,
    partProductsProcessesCost,
    processesCost: partProcessesCost * seriesMultiplier,
    productsProcessesCost: partProductsProcessesCost * seriesMultiplier,
  };

  const margins = {
    addedValueProcessesMargin: partAddedValueProcessesMarginPrice * seriesMultiplier,
    generalMarginPrice: partGeneralMarginPrice * seriesMultiplier,
    materialMarginPrice: partMaterialMarginPrice * seriesMultiplier,
    partAddedValueProcessesMarginPrice,
    partMaterialMarginPrice,
    partProductsProcessesMarginPrice,
    processesMargin: partProcessesMarginPrice * seriesMultiplier,
    productsProcessesMargin: partProductsProcessesMarginPrice * seriesMultiplier,
  };

  const prices = {
    addedValueProcessesPrice: partAddedValueProcessesPrice * seriesMultiplier,
    generalPrice: partGeneralPrice * seriesMultiplier,
    materialPrice: partMaterialPrice * seriesMultiplier,
    partAddedValueProcessesPrice,
    partMaterialPrice,
    partProductsProcessesPrice,
    processesPrice: partProcessesPrice * seriesMultiplier,
    productsProcessesPrice: partProductsProcessesPrice * seriesMultiplier,
  };

  const assembly = {
    addedValueProcessesCost: assemblyAddedValueProcessesCost,
    productsProcessesCost: assemblyProductsProcessesCost,
    materialCost: assemblyMaterialCost,
    totalCost: assemblyCost,
    totalMargin: assemblyMargin,
    totalPrice: assemblyPrice,
  };

  return {
    partPrice,
    partCost,
    partMarginPrice,
    assembly,
    seriesPrice,
    seriesCost,
    seriesMarginPrice,
    costs,
    margins,
    prices,
    quantity: seriesMultiplier,
  };
};
